import noop from "lodash/noop";
import { Popconfirm, Space } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import useFormFieldMeta from "hooks/useFormFieldMeta";
import { NamePath } from "rc-field-form/lib/interface";
import { getSubfieldErrors, namePathStartsWithMatcher } from "utils/formUtils";

import {
  UsePresalePasswordFieldProps,
  PresalePasswordsListFieldValue,
  UsePresalePasswordsFieldReturnType,
} from "./types";

export const usePresalePasswordsField = ({
  value: dataSource,
  onChange = noop,
  setDisplayForm,
  setEditIndex,
  setEditValues,
}: UsePresalePasswordFieldProps): UsePresalePasswordsFieldReturnType => {
  const intl = useIntl();
  const [showPresalePasswordsModal, setShowPresalePasswordsModal] =
    useState(false);

  const handleEdit = useCallback(
    (value: PresalePasswordsListFieldValue, index: number) => {
      setEditIndex(index);
      setEditValues(value);
      setDisplayForm(true);
    },
    [setDisplayForm, setEditIndex, setEditValues]
  );

  const handleDelete = useCallback(
    (_value: PresalePasswordsListFieldValue, index: number) => {
      if (!onChange || !dataSource) {
        return;
      }

      onChange([...dataSource.slice(0, index), ...dataSource.slice(index + 1)]);
    },
    [dataSource, onChange]
  );

  return {
    table: {
      dataSource,
      actionsColumn: {
        title: intl.formatMessage({
          defaultMessage: "Action",
        }),
        key: "action",
        render: (_, value, index) => (
          <Space size="middle">
            <EditOutlined onClick={() => handleEdit(value, index)} />
            <Popconfirm
              title={intl.formatMessage(
                {
                  defaultMessage:
                    "Are you sure you want to delete Password: {name}?",
                },
                { name: value.name }
              )}
              onConfirm={() => handleDelete(value, index)}
            >
              <DeleteOutlined />
            </Popconfirm>
          </Space>
        ),
      },
    },
    closeModal: useCallback(() => {
      setShowPresalePasswordsModal(false);
    }, []),
    openModal: useCallback(() => {
      setShowPresalePasswordsModal(true);
    }, []),
    showPresalePasswordsModal,
  };
};

export const usePresalePasswordsFieldErrors = (name: NamePath) => {
  const { fieldErrors } = useFormFieldMeta(namePathStartsWithMatcher(name));

  return useMemo(
    () => getSubfieldErrors(fieldErrors, name),
    [fieldErrors, name]
  );
};
